import { fetchAPI } from '.'

import { SwitchEnterprisePlanRequest } from '@/api/generated/api'
import { EnterpriseSubscription } from '@/types'

interface RedirectResponse {
  url: string
}

// Derived from the generated client so a backend variant addition can't drift silently.
export type BusinessVariant = NonNullable<SwitchEnterprisePlanRequest['variant']>

interface CheckoutSessionRequest {
  plan: string
  seats: number
  add_billing_info?: boolean
  currency?: string
  // Absent selects the legacy plan price rather than a Business option.
  variant?: BusinessVariant
}

// Absent `variant` keeps the legacy name-based switch; present upgrades onto a Business option price.
export type SwitchPlanRequest = Omit<SwitchEnterprisePlanRequest, 'variant'> & { variant?: BusinessVariant }

export interface CheckoutSessionInfoResponse {
  url: string
  seats_charged: number
  trial_period_days: number
}

export interface Invoice {
  id: string
  stripe_invoice_id: string
  charge_on: string | null
  created_at: string | null
  amount: number
  currency: string | null
}

export interface NextInvoiceResponse {
  invoices: Invoice[]
  charge_on: string | null
  total_amount: number
  renewal_amount: number
  renewal_date: string | null
  new_seats_amount: number
  currency: string | null
}

export async function getCheckoutSessionURL(data: CheckoutSessionRequest) {
  const { currency, ...bodyData } = data
  return fetchAPI<RedirectResponse>({
    method: 'POST',
    url: '/api/v1/payment/create_enterprise_checkout_session',
    data: bodyData,
    params: currency && currency.length > 0 ? { currency } : undefined,
  })
}

export async function getCheckoutSessionInfo(data: CheckoutSessionRequest) {
  const { currency, ...bodyData } = data
  return fetchAPI<CheckoutSessionInfoResponse>({
    method: 'POST',
    url: '/api/v1/payment/create_enterprise_checkout_session',
    data: bodyData,
    params: currency && currency.length > 0 ? { currency } : undefined,
  })
}

export async function getPortalURL() {
  return fetchAPI<RedirectResponse>({
    method: 'GET',
    url: '/api/v1/payment/enterprise-portal',
  })
}

export async function switchEnterprisePlan(data: SwitchPlanRequest) {
  return fetchAPI<EnterpriseSubscription>({
    method: 'POST',
    url: '/api/v1/payment/enterprise/switch-plan',
    data,
  })
}

export async function getNextInvoice() {
  return fetchAPI<NextInvoiceResponse>({
    method: 'GET',
    url: '/next-invoice',
  })
}

export interface ProductPricing {
  product_id: string
  price_id: string
  price: number
  currency_factor: number
}

export interface BusinessOptionPricing {
  monthly: ProductPricing
  yearly: ProductPricing
}

export interface PricingData {
  FLOW_PRO_MONTHLY: ProductPricing
  FLOW_PRO_YEARLY: ProductPricing
  FLOW_BUSINESS_MONTHLY: ProductPricing
  FLOW_BUSINESS_YEARLY: ProductPricing
  // Absent whenever a currency has no complete Business option catalog.
  business_dictation_only?: BusinessOptionPricing | null
  business_bundle?: BusinessOptionPricing | null
}

export interface PricingByIpResponse {
  ip: string | null
  location: {
    ip: string
    country_code: string | null
    country_name: string | null
  } | null
  currency: string
  flag: string | null
  pricing: PricingData
}

export async function getPricingByIp() {
  return fetchAPI<PricingByIpResponse>({
    method: 'GET',
    url: '/api/v1/payment/pricing/by-ip',
  })
}
